import { cn } from '@/lib/utils'
import { MARK_LAYER_RADIUS, MARK_LAYER_SIZE, MARK_LAYERS, MARK_STROKE_WIDTH, MARK_TRIANGLE, MARK_VIEWBOX } from './markGeometry'
import { useLoopsAllowed } from './useLoopsAllowed'
import './warm-up-mark.css'

/**
 * `warm-up` loops: the layers hop in turn, bottom to top, twice like a heartbeat, then rest (waiting for something that
 * may come). `settle` plays once: the layers drop onto the stack and stay (nothing more is coming). `still` is the
 * supplied mark at rest.
 */
export type WarmUpMarkMotion = 'warm-up' | 'settle' | 'still'

/**
 * The stacked play mark with its layers free to move, for a public waiting moment such as the watch screen's canvas
 * before any camera is live. At rest it is the supplied mark exactly (`markGeometry`, as `PlayLogo`): same layers,
 * colours, outline and triangle, never stretched, rotated or recoloured; a hop is half the xxs space, so a layer never
 * reaches the one above it. Decorative: the words that say what is happening go beside it. Under reduced motion it holds
 * still (here and in its stylesheet), and the warm-up loop rests while the page is hidden.
 */
export function WarmUpMark({ motion = 'warm-up', className }: { motion?: WarmUpMarkMotion; className?: string }) {
  const allowed = useLoopsAllowed()
  const shown = !allowed.motion || (motion === 'warm-up' && !allowed.loops) ? 'still' : motion
  return (
    <span className={cn('play-warm-up', className)} data-motion={shown} aria-hidden="true">
      <svg className="play-warm-up__mark" viewBox={MARK_VIEWBOX} fill="none" stroke="var(--play-ink)" strokeWidth={MARK_STROKE_WIDTH}>
        {MARK_LAYERS.map(({ x, y, fill }, index) => (
          <g key={fill} className="play-warm-up__layer">
            <rect x={x} y={y} width={MARK_LAYER_SIZE} height={MARK_LAYER_SIZE} rx={MARK_LAYER_RADIUS} fill={fill} />
            {/* The play triangle travels with the top layer. */}
            {index === MARK_LAYERS.length - 1 && <path d={MARK_TRIANGLE} fill="var(--play-ink)" stroke="none" />}
          </g>
        ))}
      </svg>
    </span>
  )
}
